import { AlertTriangle, CheckCircle2, LoaderCircle, Undo2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiClient, type CenaRecalcRun } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { retryPauseFrom, usePolling } from "@/shared/hooks/usePolling";
import { CenaRecalcResult } from "@/features/mentions/CenaRecalcResult";
import { isCenaRecalcRunning, recalcSummary } from "@/features/mentions/cena-recalc-view";

const POLL_MS = 3000;

/**
 * O andamento e o desfecho de um recálculo do CENA na tela de menções.
 * Acompanha sozinho (polling de 3 s, pausado no 429) e avisa a página
 * quando termina (`onSettled`), pra lista recarregar em silêncio.
 *
 * Desfazer só aparece com `can_undo`; sem ele, o servidor diz por quê
 * (`cannot_undo_reason`) e a frase vai na tela.
 */
export function CenaRecalcBanner({
  token,
  run: initial,
  onSettled,
  onUndone,
  onDismiss
}: {
  token: string;
  run: CenaRecalcRun;
  /** Terminou (ou errou): a lista mostra a nota nova. */
  onSettled: (run: CenaRecalcRun) => void;
  /** O desfazer foi aceito: a página passa a acompanhar o recálculo novo. */
  onUndone: (run: CenaRecalcRun) => void;
  onDismiss: () => void;
}) {
  const [run, setRun] = useState(initial);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  // Outro recálculo (o desfazer, ou o link do sino) troca a execução.
  useEffect(() => {
    setRun(initial);
    setPausedUntil(null);
    setUndoError(null);
    // Uma execução que chegou JÁ terminada (link do sino) também avisa a
    // lista; as que terminam ao vivo avisam no tick.
    if (!isCenaRecalcRunning(initial)) settledRef.current(initial);
  }, [initial]);

  const running = isCenaRecalcRunning(run);

  usePolling({
    enabled: running,
    intervalMs: POLL_MS,
    onTick: () => {
      apiClient
        .getCenaRecalc(token, run.id)
        .then(next => {
          setRun(next);
          if (!isCenaRecalcRunning(next)) settledRef.current(next);
        })
        .catch(failure => setPausedUntil(retryPauseFrom(failure)));
    },
    signature: `${run.id}:${run.status}:${run.processed}`,
    pausedUntil
  });

  const undo = async () => {
    setUndoing(true);
    try {
      const next = await apiClient.undoCenaRecalc(token, run.id);
      setConfirmingUndo(false);
      onUndone(next);
    } catch (failure) {
      setConfirmingUndo(false);
      setUndoError(errorMessage(failure, "Não foi possível desfazer o recálculo."));
    } finally {
      setUndoing(false);
    }
  };

  const total = run.total.toLocaleString("pt-BR");

  if (running) {
    return (
      <section className="card bulk-banner" aria-live="polite">
        <p className="bulk-banner-head">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          {run.undo_of_id !== null ? "Desfazendo o recálculo do CENA" : "Recalculando o CENA"}…{" "}
          {run.processed.toLocaleString("pt-BR")} de {total} processadas
        </p>
      </section>
    );
  }

  if (run.status === "ERRORED") {
    return (
      <section className="card bulk-banner is-error" aria-live="polite">
        <p className="bulk-banner-head">
          <AlertTriangle size={16} aria-hidden="true" />
          O recálculo do CENA parou com erro{run.message ? `: ${run.message}` : "."}{" "}
          {run.processed > 0 &&
            `${run.processed.toLocaleString("pt-BR")} de ${total} menções foram processadas antes.`}
        </p>
        <div className="bulk-banner-actions">
          <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
            <X size={16} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`card bulk-banner ${run.failed ? "has-failures" : "is-done"}`}
      aria-live="polite"
    >
      <p className="bulk-banner-head">
        <CheckCircle2 size={16} aria-hidden="true" />
        {recalcSummary(run)}
      </p>
      <div className="bulk-banner-actions">
        <button type="button" className="text-button" onClick={() => setDetailOpen(true)}>
          Ver detalhes
        </button>
        {run.can_undo && (
          <button type="button" className="text-button" onClick={() => setConfirmingUndo(true)}>
            <Undo2 size={14} aria-hidden="true" /> Desfazer
          </button>
        )}
        <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
          <X size={16} />
        </button>
      </div>
      {!run.can_undo && run.cannot_undo_reason && (
        <p className="ai-reprocess-hint">{run.cannot_undo_reason}</p>
      )}
      {undoError && (
        <div className="account-feedback error" role="alert">
          {undoError}
        </div>
      )}
      {detailOpen && (
        <CenaRecalcResult token={token} run={run} onClose={() => setDetailOpen(false)} />
      )}
      {confirmingUndo && (
        <ConfirmDialog
          title="Desfazer o recálculo?"
          description="As menções voltam às notas que tinham antes deste recálculo. Menções alteradas depois dele ficam como estão."
          confirmLabel="Desfazer"
          tone="warning"
          loading={undoing}
          onConfirm={() => void undo()}
          onCancel={() => setConfirmingUndo(false)}
        />
      )}
    </section>
  );
}
